import { useDispatch, useSelector } from 'react-redux';
import {
  listPractitioners,
  deletePractitioner,
  updatePractitionerRoles,
} from 'src/store/actions/practitioner.action';
import { getAllFronteggRoles } from 'src/modules/SuperAdmin/store/actions/practitioner.action';
import { Button, Table, Input, Tag, Modal, Alert, Select, Form } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useEffect, useState, useMemo } from 'react';
import {
  InvitePractitioner,
  InviteUser,
} from '../../../../components/groupComponents/inviteUser';
import { usePermissionEntitlements } from '@frontegg/react';

const getPublicRoles = (fronteggRoles) => {
  const publicRoles = ['admin', 'user', 'clinician', 'medical-assistant'];

  return (fronteggRoles || [])?.filter((role) =>
    publicRoles.includes((role?.key || '').toLowerCase().trim())
  );
};

export const Users = () => {
  const dispatch = useDispatch();
  const { practitionerList, currentPractitioner, practitionerListLoading } =
    useSelector((state) => state?.practitionerState);
  const { fronteggRolesAll } = useSelector((state) => state?.doctorsState);
  const { isEntitled: canDeleteUser } =
    usePermissionEntitlements('user.delete');
  const isAdmin = currentPractitioner?.roles?.some((role) =>
    ['admin', 'super-admin'].includes(role)
  );
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = window.innerWidth <= 1260;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [showRoleEditModal, setShowRoleEditModal] = useState(false);
  const [editingPractitioner, setEditingPractitioner] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roleUpdateLoading, setRoleUpdateLoading] = useState(false);

  const onDeletePractitioner = (practitioner) => {
    setSelectedPractitioner(practitioner);
    setShowDeleteModal(true);
  };

  const onEditRoles = (practitioner) => {
    setEditingPractitioner(practitioner);
    // Pre-populate with current role names
    const currentRoleNames =
      practitioner?.frontegg_roles
        ?.map((role) => role.name || role)
        .filter(Boolean) || [];
    setSelectedRoles(currentRoleNames);
    setShowRoleEditModal(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedPractitioner) {
      dispatch(deletePractitioner(selectedPractitioner.practitioner_id));
      setSelectedPractitioner(null);
    }
    setShowDeleteModal(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedPractitioner(null);
  };

  const handleRoleUpdateCancel = () => {
    setShowRoleEditModal(false);
    setEditingPractitioner(null);
    setSelectedRoles([]);
  };

  const handleRoleUpdateSave = () => {
    if (editingPractitioner && selectedRoles.length > 0) {
      setRoleUpdateLoading(true);
      analytics.track('User Updated Roles in Users Page', {
        practitioner_id: editingPractitioner.practitioner_id,
        roles: selectedRoles,
      });
      dispatch(
        updatePractitionerRoles(
          editingPractitioner.practitioner_id,
          selectedRoles,
          (success) => {
            setRoleUpdateLoading(false);
            if (success) {
              setShowRoleEditModal(false);
              setEditingPractitioner(null);
              setSelectedRoles([]);
            }
          }
        )
      );
    }
  };

  const fronteggRoleOptions = useMemo(() => {
    if (fronteggRolesAll && Array.isArray(fronteggRolesAll)) {
      return getPublicRoles(fronteggRolesAll).map((role) => ({
        value: role.name,
        label: role.name,
      }));
    }
    return [];
  }, [fronteggRolesAll]);

  const baseColumns = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      render: (text, record) => (
        <div>
          <div>{record.full_name}</div>
          <div className="text-gray-400 text-sm">
            {record.practitioner_email}
          </div>
        </div>
      ),
    },
    {
      title: 'Roles',
      render: (text, record) => {
        return (
          <div
            className={`flex ${
              isMobile ? 'flex-col' : 'flex-row'
            } gap-1 items-center`}
          >
            <div
              className={`flex ${
                isMobile ? 'flex-col' : 'flex-row'
              } gap-1 flex-wrap`}
            >
              {record?.frontegg_roles?.map((role) => {
                const roleText = role.name || role;
                return (
                  <span key={role.id || roleText}>
                    <Tag
                      className="mr-2"
                      color={
                        roleText.toLowerCase() === 'admin'
                          ? '#00d090'
                          : undefined
                      }
                    >
                      {roleText}
                    </Tag>
                  </span>
                );
              })}
            </div>
            {isAdmin &&
              record.practitioner_id !==
                currentPractitioner?.practitioner_id && (
                <EditOutlined
                  onClick={() => onEditRoles(record)}
                  className="cursor-pointer"
                />
              )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    dispatch(listPractitioners(true));
    dispatch(getAllFronteggRoles());
  }, [dispatch]);

  const filteredPractitionerList = useMemo(() => {
    if (!searchTerm) {
      return practitionerList;
    }
    return practitionerList?.filter(
      (practitioner) =>
        practitioner.full_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        practitioner.practitioner_email
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
  }, [practitionerList, searchTerm]);

  return (
    <div className="mt-5 lg:w-2/3">
      <div className="settings-component p-5 shadow-xl bg-white rounded-xl">
        <h1 className="text-black font-bold text-[18px]">Manage users</h1>
        <div className="flex justify-between mb-4">
          <div className="w-2/3">
            <Input.Search
              placeholder="Search practitioners by name or email"
              onSearch={(value) => setSearchTerm(value)}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </div>
          <div className="w-[1/3]">
            <InviteUser mobile={isMobile} />
          </div>
        </div>
        <div className="w-full">
          <Table
            dataSource={filteredPractitionerList}
            columns={baseColumns}
            loading={practitionerListLoading}
            pagination={false}
            className="w-full"
            rowKey="practitioner_id"
          />
        </div>
      </div>

      {/* Delete Modal */}
      <Modal
        title="Confirm Deletion"
        open={showDeleteModal}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete the user{' '}
          <strong>{selectedPractitioner?.full_name}</strong>?
        </p>
        <Alert
          message="This action cannot be undone."
          type="error"
          showIcon
          className="mt-4"
        />
      </Modal>

      {/* Role Edit Modal */}
      <Modal
        title={
          <span className="font-bold text-lg">
            Edit Roles for {editingPractitioner?.full_name}
          </span>
        }
        open={showRoleEditModal}
        onCancel={handleRoleUpdateCancel}
        footer={[
          <Button key="cancel" onClick={handleRoleUpdateCancel}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleRoleUpdateSave}
            loading={roleUpdateLoading}
            disabled={selectedRoles.length === 0}
          >
            Save
          </Button>,
        ]}
        width={600}
        destroyOnClose
        centered
      >
        <div className="py-4">
          <div className="mb-2 font-semibold">Roles</div>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select roles"
            value={selectedRoles}
            onChange={setSelectedRoles}
            options={fronteggRoleOptions}
            optionLabelProp="label"
          />
          {selectedRoles.length === 0 && (
            <div className="text-red-500 text-sm mt-2">
              At least one role must be selected
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Users;
